import React, { useEffect, useRef, useState } from "react";
import { X, Zap, Keyboard, ScanLine } from "lucide-react";
import { Button } from "./ui/kit";
import { dekodujSnimek } from "../lib/carovyKod";

/**
 * Čtečka čárových kódů z mobilu.
 *
 * Kde je `BarcodeDetector` (Android Chrome, Chrome na PC), použije se — přečte
 * i QR a Code128. Kde není (iOS Safari), nastoupí vlastní dekodér EAN/UPC
 * z `lib/carovyKod.js`, takže skenování funguje i na iPhonu bez knihovny navíc.
 * Ruční zadání zůstává pro případ olepeného kódu a obsluhuje i bluetooth
 * čtečky: ty se tváří jako klávesnice a kód potvrdí Enterem.
 */
export const nativniDetektor = () =>
  typeof window !== "undefined" && "BarcodeDetector" in window;

/** Kamera je jediná podmínka — dekódovat umíme vždycky. */
export const skenerPodporovan = () =>
  typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

// Kód luštíme z vodorovného pásu uprostřed obrazu — přesně tam, kam míří hledáček.
const PAS = { sirka: 720, vyska: 220, odshora: 0.35, vyska_podil: 0.3 };

const FORMATY = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"];

/** Stejný kód pod objektivem se přečte desetkrát za vteřinu — bereme ho jednou. */
const PAUZA_MS = 2000;

export function SkenerSheet({ onKod, onClose, titulek = "Naskenuj kód", napoveda, pauza = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const platnoRef = useRef(null);
  const posledniRef = useRef({ kod: "", kdy: 0 });
  // Když je nad skenerem otevřená karta položky, čtení stojí — jinak by
  // další kód v záběru přepsal to, co uživatel zrovna potvrzuje.
  const pauzaRef = useRef(pauza);
  pauzaRef.current = pauza;
  const [rucne, setRucne] = useState(!skenerPodporovan());
  const [kod, setKod] = useState("");
  const [chyba, setChyba] = useState("");
  const [svitilna, setSvitilna] = useState(false);
  const [maSvitilnu, setMaSvitilnu] = useState(false);

  useEffect(() => {
    if (rucne) return undefined;
    let bezi = true;
    let timer = null;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // vyšší rozlišení = víc pixelů na proužek, což vlastní dekodér potřebuje
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (!bezi) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        const track = stream.getVideoTracks()[0];
        setMaSvitilnu(!!track?.getCapabilities?.().torch);

        const detektor = nativniDetektor() ? new window.BarcodeDetector({ formats: FORMATY }) : null;
        if (!detektor && !platnoRef.current) {
          platnoRef.current = document.createElement("canvas");
          platnoRef.current.width = PAS.sirka;
          platnoRef.current.height = PAS.vyska;
        }

        const precti = async () => {
          const video = videoRef.current;
          if (!video || video.readyState < 2) return null;
          if (detektor) {
            const nalezene = await detektor.detect(video);
            return nalezene[0]?.rawValue?.trim() || null;
          }
          const ctx = platnoRef.current.getContext("2d", { willReadFrequently: true });
          const sh = video.videoHeight * PAS.vyska_podil;
          ctx.drawImage(video, 0, video.videoHeight * PAS.odshora, video.videoWidth, sh,
                        0, 0, PAS.sirka, PAS.vyska);
          const obraz = ctx.getImageData(0, 0, PAS.sirka, PAS.vyska);
          return dekodujSnimek(obraz.data, PAS.sirka, PAS.vyska);
        };

        timer = setInterval(async () => {
          if (!bezi || pauzaRef.current) return;
          try {
            const prvni = await precti();
            if (!prvni) return;
            const ted = Date.now();
            const p = posledniRef.current;
            if (p.kod === prvni && ted - p.kdy < PAUZA_MS) return;
            posledniRef.current = { kod: prvni, kdy: ted };
            navigator.vibrate?.(60);
            onKod(prvni);
          } catch { /* jeden neúspěšný snímek nevadí, zkusí se další */ }
        }, 220);
      } catch (e) {
        setChyba(e?.name === "NotAllowedError"
          ? "Kamera je zakázaná. Povol ji v nastavení prohlížeče, nebo zadej kód ručně."
          : "Kameru se nepodařilo spustit. Zadej kód ručně.");
        setRucne(true);
      }
    })();

    return () => {
      bezi = false;
      if (timer) clearInterval(timer);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [rucne, onKod]);

  const prepniSvitilnu = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !svitilna }] });
      setSvitilna((s) => !s);
    } catch { /* svítilnu neumí každý telefon */ }
  };

  const potvrdRucne = (e) => {
    e.preventDefault();
    const v = kod.trim();
    if (!v) return;
    setKod("");
    onKod(v);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink flex flex-col" data-testid="skener">
      <div className="flex items-center gap-3 px-4 py-3 shrink-0">
        <ScanLine size={18} className="text-accent" />
        <span className="text-[14px] font-700 text-white">{titulek}</span>
        {maSvitilnu && !rucne && (
          <button onClick={prepniSvitilnu} data-testid="skener-svitilna"
            className={`ml-auto h-9 w-9 rounded-icon flex items-center justify-center ${
              svitilna ? "bg-accent text-white" : "bg-white/10 text-white"}`}>
            <Zap size={16} />
          </button>
        )}
        <button onClick={onClose} data-testid="skener-zavrit"
          className={`h-9 w-9 rounded-icon bg-white/10 text-white flex items-center justify-center ${maSvitilnu && !rucne ? "" : "ml-auto"}`}>
          <X size={18} />
        </button>
      </div>

      {rucne ? (
        <form onSubmit={potvrdRucne} className="flex-1 flex flex-col justify-center px-6 gap-3">
          <label className="label-up text-white/50">Kód položky</label>
          {/* autoFocus je tu schválně: bluetooth čtečka „píše" rovnou do pole */}
          <input autoFocus value={kod} onChange={(e) => setKod(e.target.value)}
            inputMode="text" placeholder="EAN nebo katalogové číslo" data-testid="skener-rucne"
            className="w-full bg-white/10 border border-white/20 rounded-input px-4 h-12 text-[16px] text-white outline-none focus:border-accent" />
          <Button type="submit" className="h-11 justify-center">Načíst</Button>
          <p className="text-[12px] text-white/50 leading-relaxed">
            {chyba || "Opiš kód z krabice, nebo použij bluetooth čtečku — ta píše rovnou "
              + "do pole a potvrdí Enterem."}
          </p>
          {!chyba && skenerPodporovan() && (
            <button type="button" onClick={() => setRucne(false)} data-testid="skener-zpet-kamera"
              className="text-[12.5px] font-700 text-accent self-start">
              Zpět na kameru
            </button>
          )}
        </form>
      ) : (
        <>
          <div className="relative flex-1 overflow-hidden">
            <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" />
            {/* hledáček — kód stačí dostat mezi rohy, ostření řeší telefon */}
            <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 h-[160px] border-2 border-white/70 rounded-card" />
            <div className="absolute inset-x-8 top-1/2 h-[2px] bg-accent" />
          </div>
          <div className="px-5 py-4 shrink-0 flex items-center gap-3">
            <p className="text-[12px] text-white/60 flex-1">
              {napoveda || "Namiř na čárový kód."}
              {!nativniDetektor() && " Čteme EAN a UPC."}
            </p>
            <button onClick={() => setRucne(true)} data-testid="skener-prepni-rucne"
              className="text-[12px] font-700 text-white inline-flex items-center gap-1.5">
              <Keyboard size={14} /> Zadat ručně
            </button>
          </div>
        </>
      )}
    </div>
  );
}
